import { LoaderOverlay } from "@components/spinner";
import { NoDataComponent } from "@components/no-data-component";
import Paginate from "@components/ui-components/paginate";
import { ROW_PER_PAGINATION_OPTIONS } from "@src/utility/constants";
import { STAGE_MAP } from "@src/views/panel/companies/misc/constants";
import capitalize from "lodash/capitalize";
import classNames from "classnames";
import dayjs from "dayjs";
import { getPlural } from "@src/utility/helpers/utils";
import size from "lodash/size";
import useDateHandler from "@src/utility/hooks/use-date-handler";
import useTimeline from "./use-timeline";
import { useTranslation } from "react-i18next";

const Timeline = () => {
    const {
        data: { timelines, pagination },
        loading,
        handlePageChange,
        handleLimit
    } = useTimeline();

    const { t } = useTranslation();

    const { dateFormatter, isValidDate } = useDateHandler();
    function getIconProps (type) {
        switch (type) {
        case "stage_update":
            return {
                icon : "mdi mdi-progress-clock",
                color: "bg-light-info"
            };
        case "client_update":
            return {
                icon : "mdi mdi-account-edit",
                color: "bg-light-secondary"
            };
        case "client_create":
            return { icon: "mdi mdi-account-plus", color: "bg-light-info" };
        case "people_add":
            return {
                icon : "mdi mdi-account-multiple-plus",
                color: "bg-light-primary"
            };
        default:
            return {
                icon : "mdi mdi-information-outline",
                color: "bg-light-muted"
            };
        }
    }

    const formatValue = (item = {}, value) => {
        if (item?.attribute === "stage") {
            return STAGE_MAP[value];
        }
        if (Array.isArray(value)) {
            return value.length ? value.join(", ") : "N/A";
        }

        if (value instanceof Date && !isNaN(value.getTime())) {
            return dateFormatter({
                date    : dayjs(value).toDate(),
                formatAs: "DD MMM YYYY"
            });
        }

        if (isValidDate(value)) {
            return dateFormatter({
                date    : dayjs(value).toDate(),
                formatAs: "DD MMM YYYY"
            });
        }

        if (typeof value === "boolean") {
            return value ? "True" : "False";
        }

        if (typeof value === "number") {
            return value.toString();
        }

        if (typeof value === "string") {
            return value.trim() ? value : "N/A";
        }

        return value !== null ? String(value) : "N/A";
    };
    return (
        <LoaderOverlay
            active={loading}
            children={
                <>
                    <h2>
                        {t(getPlural({ text: "Timeline", length: pagination.count }))} (
                        {pagination.count})
                    </h2>
                    {size(timelines) ? (
                        <>
                            <ul className="timeline d-grid gap-2 mt-2">
                                {timelines.map((item, i) => {
                                    const { icon, color } = getIconProps(
                                        item.type
                                    );
                                    return (
                                        <li className="mb-1">
                                            {size(timelines) !== i + 1 ? (
                                                <div className="bottom-line" />
                                            ) : null}
                                            <div className="d-flex align-items-center gap-1 timeline-item rounded">
                                                <div>
                                                    <span
                                                        className={classNames(
                                                            "timeline-point",
                                                            {
                                                                [`timeline-point-${item.color}`]:
                                                                    item.color
                                                            }
                                                        )}>
                                                        <i
                                                            className={classNames(
                                                                `font-medium-5 ${icon} ${color} rounded-circle p-50`
                                                            )}/>
                                                    </span>
                                                </div>
                                                <div className="timeline-event">
                                                    <div
                                                        className={classNames(
                                                            "d-flex justify-content-between flex-column text-overflow-anywhere",
                                                            {
                                                                "mb-sm-0 mb-75":
                                                                    item.meta
                                                            }
                                                        )}>
                                                        <p className="text-muted font-small-4">
                                                            {item.event}
                                                        </p>

                                                        <p>
                                                            <b>{t("Value")}:</b>{" "}
                                                            {formatValue(
                                                                item,
                                                                item.value
                                                            )}
                                                        </p>

                                                        <p>
                                                            <b>
                                                                {t(
                                                                    "Previous Value"
                                                                )}
                                                                :
                                                            </b>{" "}
                                                            {formatValue(
                                                                item,
                                                                item.previous_value
                                                            )}
                                                        </p>

                                                        {item.user && (
                                                            <p className="text-muted font-small-4 fw-bolder">
                                                                By:{" "}
                                                                {capitalize(
                                                                    item.user
                                                                        .name
                                                                )}
                                                            </p>
                                                        )}
                                                        <p className="text-muted mt-75 font-small-5">
                                                            {dateFormatter({
                                                                date: new Date(
                                                                    item.date
                                                                ),
                                                                formatAs:
                                                                    "DD MMM YYYY, hh:mm A"
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                            <Paginate
                                paginationRowsPerPageOptions={
                                    ROW_PER_PAGINATION_OPTIONS
                                }
                                className="position-sticky bottom-0"
                                handlePageChange={handlePageChange}
                                currentPage={pagination.page}
                                handleLimit={handleLimit}
                                {...pagination}/>
                        </>
                    ) : (
                        <NoDataComponent />
                    )}
                </>
            }/>
    );
};

export default Timeline;
